import React from 'react';
import { useTranslation } from 'react-i18next';
import { Columns2, Rows2, FolderOpen, LoaderCircle, ChevronRight } from 'lucide-react';
import Tooltip from '../ui/Tooltip';
import TermIconButton from './TermIconButton';
import { PaneIcon } from './TerminalPaneHeader';

/**
 * One agent in the "+" menu. `state`: 'yes' (installed — hover shows
 * open-in-folder and open-in-split buttons), 'no' ("Not installed ›" — click opens the install
 * hint, never a failed launch), 'unknown' (detection still running: a small
 * spinner in the status slot; clicking launches, and a missing program then
 * shows the hint inside the pane). Without `onOpen` the split buttons are
 * left out; `showSplits={false}` keeps only the folder button ("Split with…"
 * picker). `onOpen('folder')` is "open, asking for the folder" even when a
 * default folder is set. `tip` replaces the installed-row tooltip.
 */
export default function AgentMenuRow({ profile, state, rowProps, hintOpen, onOpen, showSplits = true, onClickRow, tip: installedTip }) {
  const { t } = useTranslation();
  const tip =
    state === 'no'
      ? t('terminalExt.notInstalledTip', '{{name}} is not installed — show how to install it', { name: profile.name })
      : installedTip ?? t('terminalExt.agentRowTip', 'Click: new tab · Alt+click: split right · Alt+Shift+click: split below');
  return (
    <Tooltip content={tip} side="right" delay={600}>
      <div
        {...rowProps}
        className={`${rowProps.className}${state === 'no' ? ' term-menu-row--muted' : ''}`}
        onClick={onClickRow}
        aria-expanded={state === 'no' ? hintOpen : undefined}
        aria-label={state === 'no' ? `${profile.name} — ${t('terminalExt.notInstalled', 'Not installed')}` : profile.name}
      >
        <PaneIcon pane={{ icon: profile.icon, profileId: profile.id }} />
        <span className="term-menu-row-name">{profile.name}</span>
        {state === 'unknown' && <LoaderCircle size={12} className="animate-spin term-menu-row-status" aria-label={t('terminalExt.checking', 'Checking…')} />}
        {state === 'no' && (
          <span className="term-menu-row-status term-menu-row-missing">
            {t('terminalExt.notInstalled', 'Not installed')}
            <ChevronRight size={12} aria-hidden style={{ transform: hintOpen ? 'rotate(90deg)' : undefined }} />
          </span>
        )}
        {state !== 'no' && onOpen && (
          <span className="term-menu-row-actions">
            <TermIconButton className="term-menu-mini" side="bottom" label={t('terminalExt.openInFolder', 'Open in another folder…')} onClick={() => onOpen('folder')}>
              <FolderOpen size={12} />
            </TermIconButton>
            {showSplits && (
              <>
                <TermIconButton className="term-menu-mini" side="bottom" label={t('terminalExt.openSplitRight', 'Open in a split on the right')} onClick={() => onOpen('row')}>
                  <Columns2 size={12} />
                </TermIconButton>
                <TermIconButton className="term-menu-mini" side="bottom" label={t('terminalExt.openSplitDown', 'Open in a split below')} onClick={() => onOpen('col')}>
                  <Rows2 size={12} />
                </TermIconButton>
              </>
            )}
          </span>
        )}
      </div>
    </Tooltip>
  );
}
